// Service weeks are ISO Monday dates in the site's zone. Schedule now anchors on the same
// Monday (see weekStartIso), so the two no longer straddle each other.
import type { CheckpointExceptionCode, ServiceExceptionCode, ServiceProvenance, ServiceWeekCheckpointRow, ServiceWeekSummary, ServiceWeekWorkSegmentFact } from "../data/service-week-approval";
import type { ServiceWeekChangeClass, ServiceWeekDecisionAct } from "../data/service-week-decisions";

export function isServiceTimeZone(value: unknown): value is string {
  if (typeof value !== "string" || value.length > 80 || !/^[A-Za-z_]+(?:\/[A-Za-z0-9_+.-]+)*$/.test(value)) return false;
  try { new Intl.DateTimeFormat("en-US", { timeZone: value }).format(); return true; } catch { return false; }
}

export function isServiceLocalDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value && Number(value.slice(0, 4)) >= 1;
}

export function isServiceWeekStart(value: unknown): value is string {
  return isServiceLocalDate(value) && new Date(`${value}T12:00:00Z`).getUTCDay() === 1;
}

export function isServiceInstant(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,6})?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.test(value)) return false;
  return isServiceLocalDate(value.slice(0, 10)) && Number.isFinite(Date.parse(value));
}

/** Calendar arithmetic on date components only; a local date is never a site UTC boundary. */
export function addServiceWeeks(weekStart: string, delta: number): string | null {
  if (!isServiceWeekStart(weekStart) || !Number.isInteger(delta) || Math.abs(delta) > 52000) return null;
  const date = new Date(`${weekStart}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + delta * 7);
  const result = date.toISOString().slice(0, 10);
  return isServiceWeekStart(result) ? result : null;
}

export function currentServiceWeekStart(timeZone: string, now: Date = new Date()): string | null {
  if (!isServiceTimeZone(timeZone) || !Number.isFinite(now.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const part = (name: string) => parts.find((item) => item.type === name)?.value;
  const date = new Date(`${part("year")}-${part("month")}-${part("day")}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 6) % 7);
  return date.toISOString().slice(0, 10);
}

export function latestCompletedServiceWeekStart(timeZone: string, now: Date = new Date()): string | null {
  const current = currentServiceWeekStart(timeZone, now);
  return current ? addServiceWeeks(current, -1) : null;
}

export function formatServiceWeekInstant(instant: string, timeZone: string): string | null {
  if (!isServiceInstant(instant) || !isServiceTimeZone(timeZone)) return null;
  return new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(instant));
}

export function formatServiceWeekRange(weekStart: string): string | null {
  if (!isServiceWeekStart(weekStart)) return null;
  const end = new Date(`${weekStart}T12:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 6);
  return `${weekStart} to ${end.toISOString().slice(0, 10)}`;
}

export const serviceWeekCopy = {
  loadingTargets: "Loading client sites...",
  loadingReview: "Loading service-week facts...",
  targetsEmpty: "No active client sites are available for service-week approval.",
  targetsTooLarge: "There are too many client sites to list here. Ask SecOps support for the complete list.",
  unapproved: "This service week has not been approved. Review all service facts before approving it.",
  pendingUnscheduled: "Include every pending unscheduled service item before approving this week.",
  rejectedUnscheduled: "This week has rejected unscheduled service. It cannot be approved here. Ask SecOps support for help.",
  stale: "Service facts changed after approval. Review and approve this week again.",
  staleUnavailable: "Service facts changed after approval, and the current week cannot be reviewed. The prior approved facts stay frozen. Ask SecOps support for help.",
  empty: "No service was scheduled or attributable for this site and week. Nothing to approve.",
  sourceIncomplete: "This week cannot be approved because its service facts are incomplete.",
  tooLarge: "This week is too large to review here. Ask SecOps support for help.",
  factsChanged: "Service facts changed while you were reviewing. Review the refreshed week and try again.",
  candidateChanged: "This service item changed and can no longer be included. Review the refreshed week.",
  unavailable: "Service-week approval is unavailable right now. Check your access or try again.",
  receipt: "Approval recorded. Approved service facts are frozen.",
  confirmation: "This freezes the complete service record for proof of service, including the exceptions shown. It does not approve payroll hours.",
  approve: "Approve service week and freeze facts",
  include: "Include in service week",
  unresolvedEntryRejection: "Resolve every rejected entry before approving this week.",
  rejectEntry: "Reject entry",
  setStart: "Set start time",
  setEnd: "Set end time",
  rejectDescription: "This records your reason for the officer and authorized supervisors. The original entry stays on the record.",
  setDescription: "The recorded punch stays as it is. This adds a supervisor-set fact with your reason on file.",
  reasonLabel: "Reason (required, 1 to 280 characters)",
  reasonRequired: "Enter a reason of 1 to 280 characters without contact details, links, or hidden characters.",
  invalidBoundary: "Choose a valid site-local date and time. Ambiguous or skipped clock times and times outside this work segment cannot be recorded.",
  boundaryHelp: "Choose the date and time explicitly. The site time zone is used; ambiguous or skipped clock times are refused.",
  entryChanged: "This entry changed and can no longer be acted on. Review the refreshed week.",
  bulkRefused: "Rows with exceptions cannot be marked reviewed together. Work each one from the Exceptions group.",
  noCleanEntries: "No eligible rows to review.",
  bulkConfirmation: "This records review of every eligible clean entry in this service week. Week approval is a separate act.",
  decisionPending: "Recording entry decision...",
  reviewPending: "Recording review...",
  decisionRecorded: "Entry decision recorded. Review the refreshed service week.",
  reviewedRecorded: "Entry review recorded. Week approval is a separate act.",
  trailEmpty: "No decisions have been recorded for this service week.",
  durationMissing: "Recorded duration is unavailable while a boundary is missing.",
  weekDurationMissing: "A complete recorded duration is unavailable while any boundary is missing.",
} as const;

export function inProgressServiceWeekCopy(timeZone: string): string {
  return isServiceTimeZone(timeZone) ? `This service week has not ended. Approve it after Sunday ends in ${timeZone}.` : serviceWeekCopy.unavailable;
}

export function serviceWeekReasonCopy(reason: string, timeZone?: string): string {
  switch (reason) {
    case "no_active_sites": return serviceWeekCopy.targetsEmpty;
    case "targets_too_large": return serviceWeekCopy.targetsTooLarge;
    case "week_not_ended": case "week_in_progress": return timeZone ? inProgressServiceWeekCopy(timeZone) : serviceWeekCopy.unavailable;
    case "no_scheduled_service": return serviceWeekCopy.empty;
    case "week_unapproved": return serviceWeekCopy.unapproved;
    case "source_incomplete": return serviceWeekCopy.sourceIncomplete;
    case "too_large": return serviceWeekCopy.tooLarge;
    case "facts_changed": return serviceWeekCopy.factsChanged;
    case "candidate_not_current_or_eligible": return serviceWeekCopy.candidateChanged;
    case "unresolved_entry_rejection": return serviceWeekCopy.unresolvedEntryRejection;
    case "pending_unscheduled": return serviceWeekCopy.pendingUnscheduled;
    case "rejected_unscheduled": return serviceWeekCopy.rejectedUnscheduled;
    case "reason_required": return serviceWeekCopy.reasonRequired;
    case "invalid_boundary": return serviceWeekCopy.invalidBoundary;
    case "entry_unavailable": return serviceWeekCopy.entryChanged;
    case "ineligible_selection": return serviceWeekCopy.bulkRefused;
    default: return serviceWeekCopy.unavailable;
  }
}

export const serviceWeekChangeCopy: Record<ServiceWeekChangeClass, string> = {
  supervisor_set: "A supervisor set a time.", entry_rejected: "A supervisor rejected an entry.",
  officer_resubmitted: "An officer resubmitted a time.", unscheduled_service: "Unscheduled service changed.",
  scheduled_service: "Scheduled service changed.", checkpoint_record: "A checkpoint record changed.", incident: "An incident changed.",
};
export const serviceWeekDecisionCopy: Record<ServiceWeekDecisionAct, string> = {
  included_candidate: "Included candidate", rejected_entry: "Rejected entry", set_start: "Set start time", set_end: "Set end time",
  officer_resubmitted: "Officer resubmitted", marked_reviewed: "Marked reviewed", approved_week: "Approved and froze week",
};

/** Date.parse retains milliseconds only. Split fractional seconds before parsing so
 * source microseconds remain exact through timezone offsets, DST and breaks. */
export function serviceInstantMicroseconds(value: string): bigint | null {
  if (!isServiceInstant(value)) return null;
  const match = /^(.*:\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:\d{2})$/.exec(value);
  if (!match) return null;
  return BigInt(Date.parse(`${match[1]}${match[3]}`)) * 1000n + BigInt((match[2] ?? "").padEnd(6, "0"));
}
/** Q03: lossless HH:MM:SS[.fraction], summing ordered segments rather than gaps. */
export function recordedServiceDuration(segments: ReadonlyArray<Pick<ServiceWeekWorkSegmentFact, "recordedStart" | "recordedEnd">>): string | null {
  if (segments.length === 0) return null;
  let total = 0n;
  for (const segment of segments) {
    if (segment.recordedStart === null || segment.recordedEnd === null) return null;
    const start = serviceInstantMicroseconds(segment.recordedStart), end = serviceInstantMicroseconds(segment.recordedEnd);
    if (start === null || end === null || end < start) return null;
    total += end - start;
  }
  const seconds = total / 1000000n;
  const fraction = String(total % 1000000n).padStart(6, "0").replace(/0+$/, "");
  return `${String(seconds / 3600n).padStart(2, "0")}:${String(seconds / 60n % 60n).padStart(2, "0")}:${String(seconds % 60n).padStart(2, "0")}${fraction ? `.${fraction}` : ""}`;
}

const provenanceWords: Record<ServiceProvenance, string> = {
  device_verified: "✓ device-verified", officer_entered: "e officer-entered",
  supervisor_set: "s supervisor-set · reason on file", missing: "- missing",
};
const exceptionWords: Record<ServiceExceptionCode, string> = {
  cancelled_after_publish: "Cancelled after publish", uncovered: "Uncovered", partial: "Partial service",
  missing_start: "Missing start", missing_end: "Missing end", late_start: "Late start", early_end: "Early end",
  officer_entered: "Officer-entered time", supervisor_set: "Supervisor-set time", unscheduled: "Unscheduled service",
};
const checkpointWords: Record<CheckpointExceptionCode, string> = {
  duplicate: "Duplicate check-in", out_of_window: "Outside required window", review_required: "Review required",
  offline_recorded: "Offline check-in recorded", manual_recorded: "Manual check-in recorded", fallback_used: "Fallback method used",
  rejected: "Not validated", out_of_plan: "Not on frozen route",
};
export function serviceProvenanceCopy(provenance: ServiceProvenance): string { return provenanceWords[provenance]; }
export function serviceExceptionCopy(code: ServiceExceptionCode): string { return exceptionWords[code]; }
export function serviceCheckpointCopy(row: ServiceWeekCheckpointRow): string {
  if (row.kind === "recorded_exception") return `! ${checkpointWords[row.exceptionCode]}`;
  return row.validationStatus === "device_verified" ? "✓ device-verified" : "- missing · ! Missed check-in";
}
export const serviceSummaryLabels: Record<keyof ServiceWeekSummary, string> = {
  coverageRowCount: "Service rows", publishedSeatCount: "Published seats", coveredSeatCount: "Seats with recorded service",
  uncoveredSeatCount: "Uncovered seats", missingBoundaryCount: "Missing time boundaries", exceptionCount: "Service exceptions",
  pendingUnscheduledCount: "Pending unscheduled items", includedUnscheduledCount: "Included unscheduled items",
  rejectedUnscheduledCount: "Rejected unscheduled items", workSegmentCount: "Work segments", expectedCheckInCount: "Expected check-ins",
  verifiedCheckInCount: "Verified check-ins", missedCheckInCount: "Missed check-ins", checkpointFactCount: "Checkpoint facts",
  checkpointExceptionCount: "Checkpoint exceptions", incidentCount: "Incidents",
};
